import EverTrue from "app";
import { addMillisecondsToTimestamp, formatDateFromTimestamp } from "../../utils/helpers";
import { useEffect, useState } from "react";
import { useOpenClose } from "@evertrue/et-components";
import {
  resumePauseTitle,
  resumePauseBody,
  resumePauseAction,
  cancelPauseTitle,
  cancelPauseBody,
  cancelPauseAction,
  cadenceStartMsgDefault,
  cadenceStartMsgImt,
  cadencesPaused,
  cadenceEndMsgDefault,
} from "../../utils/constants/constants";

const useJourneyTimeoutsForm = (inProgressJourneyTimeout, futureJourneyTimeout) => {
  const [startMessage, setStartMessage] = useState();
  const [endMessage, setEndMessage] = useState();
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);

  const [disableSave, setDisableSave] = useState(false);

  const [modalTitle, setModalTitle] = useState("");
  const [modalBody, setModalBody] = useState("");
  const [modalAction, setModalAction] = useState("");
  const [isModalOpen, openModal, closeModal] = useOpenClose();

  const todayTime = Math.floor(new Date().getTime() / 86400000);

  // START TIME & END TIME STATE
  useEffect(() => {
    const activeTimeout = inProgressJourneyTimeout || futureJourneyTimeout;

    if (activeTimeout) {
      setDisableSave((prevDisableSave) => !prevDisableSave);
      const { pause_start, pause_end } = activeTimeout;
      setSelectedStartDate(addMillisecondsToTimestamp(pause_start));
      setSelectedEndDate(addMillisecondsToTimestamp(pause_end));
    }
  }, [inProgressJourneyTimeout, futureJourneyTimeout]);

  // START MESSAGE STATE
  useEffect(() => {
    const selectedDateTime = selectedStartDate ? Math.floor(new Date(selectedStartDate).getTime() / 86400000) : null;

    if (inProgressJourneyTimeout) {
      setStartMessage(cadencesPaused);
    } else if (!selectedStartDate || futureJourneyTimeout) {
      setStartMessage(cadenceStartMsgDefault);
    } else if (selectedDateTime && selectedDateTime === todayTime) {
      setStartMessage(cadenceStartMsgImt);
    } else {
      setStartMessage("Your cadences will be paused starting on " + formatDateFromTimestamp(selectedStartDate) + ".");
    }
  }, [inProgressJourneyTimeout, futureJourneyTimeout, selectedStartDate, todayTime]);

  // END MESSAGE STATE
  useEffect(() => {
    if (!selectedEndDate) {
      setEndMessage(cadenceEndMsgDefault);
    } else {
      setEndMessage("Your cadences will resume on " + formatDateFromTimestamp(selectedEndDate) + ".");
    }
  }, [selectedEndDate]);

  const handleEndDateClick = (day) => {
    setDisableSave(false);
    const selectedDay = Math.floor(new Date(day).getTime() / 86400000);
    const selectedStartDay = selectedStartDate ? Math.floor(new Date(selectedStartDate).getTime() / 86400000) : null;

    if (selectedStartDay && selectedDay <= selectedStartDay) {
      return EverTrue.Alert.error("End Date cannot be on or before the Start Date");
    } else if (selectedDay < todayTime) {
      return EverTrue.Alert.error("End Date cannot be before today");
    } else setSelectedEndDate(day);
  };

  const handleStartDateClick = (day) => {
    setDisableSave(false);
    const selectedDay = Math.floor(new Date(day).getTime() / 86400000);
    const selectedEndDay = selectedEndDate ? Math.floor(new Date(selectedEndDate).getTime() / 86400000) : null;

    if (selectedDay < todayTime) {
      EverTrue.Alert.error("Start Date cannot be before today");
    } else if (selectedEndDay && selectedDay >= selectedEndDay) {
      EverTrue.Alert.error("Start Date cannot be on or after the End Date");
    } else setSelectedStartDate(day);
  };

  const handleResumeClick = () => {
    setModalAction(resumePauseAction);
    setModalTitle(resumePauseTitle);
    setModalBody(resumePauseBody);
    openModal();
  };

  const handleCancelClick = () => {
    setModalAction(cancelPauseAction);
    setModalTitle(cancelPauseTitle);
    setModalBody(cancelPauseBody);
    openModal();
  };

  return {
    startMessage,
    selectedStartDate,
    endMessage,
    selectedEndDate,
    disableSave,

    modalTitle,
    modalBody,
    modalAction,
    isModalOpen,
    closeModal,

    handleEndDateClick,
    handleStartDateClick,
    handleCancelClick,
    handleResumeClick,
  };
};

export default useJourneyTimeoutsForm;
